// src/pages/DesignationMaster.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
} from 'reactstrap';
import { Plus, Edit, Trash2, RefreshCw, Eye, CheckCircle, XCircle, Archive } from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from "../../Components/Common/TableContainer";
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// Types
interface Designation {
    id: number;
    title: string;
    description?: string;
    status: boolean;
    created_at: string;
    updated_at: string;
}

const DesignationMaster: React.FC = () => {
    // State
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // Modal states
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [selectedDesignation, setSelectedDesignation] = useState<Designation | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete modal
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [designationToDelete, setDesignationToDelete] = useState<Designation | null>(null);

    // View modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingDesignation, setViewingDesignation] = useState<Designation | null>(null);

    // Fetch designations
    const fetchDesignations = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiClient.get(`/designations?include_inactive=${showInactive}`);
            console.log('API Response:', response); // Debugging line
            const data = response.data as Designation[];
            setDesignations(data || []);
        } catch (error: any) {
            console.error('Error fetching designations:', error);
            setError(error.message || 'Failed to fetch designations');
            toast.error('Failed to load designations');
        } finally {
            setLoading(false);
        }
    }, [showInactive]);

    // Initial fetch
    useEffect(() => {
        fetchDesignations();
    }, [fetchDesignations]);

    // Handle form input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Open add modal
    const openAddModal = () => {
        setModalMode('add');
        setFormData({
            title: '',
            description: '',
            status: true,
        });
        setFormError('');
        setModal(true);
    };

    // Open edit modal
    const openEditModal = (designation: Designation) => {
        setModalMode('edit');
        setSelectedDesignation(designation);
        setFormData({
            title: designation.title,
            description: designation.description || '',
            status: designation.status,
        });
        setFormError('');
        setModal(true);
    };

    // Close modal
    const closeModal = () => {
        setModal(false);
        setSelectedDesignation(null);
        setFormError('');
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate
        if (!formData.title.trim()) {
            setFormError('Designation title is required');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            if (modalMode === 'add') {
                await apiClient.post('/designations', {
                    title: formData.title.trim(),
                    description: formData.description.trim() || undefined,
                    status: formData.status,
                });
                toast.success('Designation created successfully!');
            } else {
                await apiClient.put(`/designations/${selectedDesignation?.id}`, {
                    title: formData.title.trim(),
                    description: formData.description.trim() || undefined,
                    status: formData.status,
                });
                toast.success('Designation updated successfully!');
            }

            closeModal();
            fetchDesignations();
        } catch (error: any) {
            console.error('Error saving designation:', error);
            setFormError(error.message || 'Failed to save designation');
            toast.error('Failed to save designation');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const openDeleteModal = (designation: Designation) => {
        setDesignationToDelete(designation);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!designationToDelete) return;

        setDeleting(true);
        try {
            await apiClient.delete(`/designations/${designationToDelete.id}`);
            toast.success('Designation deleted successfully!');
            setDeleteModal(false);
            fetchDesignations();
        } catch (error: any) {
            console.error('Error deleting designation:', error);
            toast.error(error.message || 'Failed to delete designation');
        } finally {
            setDeleting(false);
            setDesignationToDelete(null);
        }
    };

    // Handle restore
    const handleRestore = async (designation: Designation) => {
        try {
            await apiClient.patch(`/designations/${designation.id}/restore`);
            toast.success('Designation restored successfully!');
            fetchDesignations();
        } catch (error: any) {
            console.error('Error restoring designation:', error);
            toast.error(error.message || 'Failed to restore designation');
        }
    };

    // Handle view
    const handleView = (designation: Designation) => {
        setViewingDesignation(designation);
        setViewModal(true);
    };

    // Format date
    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy HH:mm');
        } catch {
            return 'N/A';
        }
    };

    // Get status badge
    const getStatusBadge = (status: boolean) => {
        return status ? (
            <Badge color="success">
                <CheckCircle size={12} className="me-1" />
                Active
            </Badge>
        ) : (
            <Badge color="warning">
                <XCircle size={12} className="me-1" />
                Inactive
            </Badge>
        );
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    return <strong>{cell.row.index + 1}</strong>;
                },
            },
            {
                header: 'Title',
                accessorKey: 'title',
                cell: (cell: any) => (
                    <span className="fw-semibold">{cell.getValue()}</span>
                ),
            },
            {
                header: 'Description',
                accessorKey: 'description',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },

            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const designation = cell.row.original;
                    return (
                        <div className="d-flex gap-1">
                            <Button
                                color="info"
                                size="sm"
                                title="View"
                                onClick={() => handleView(designation)}
                            >
                                <Eye size={14} />
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                title="Edit"
                                onClick={() => openEditModal(designation)}
                            >
                                <Edit size={14} />
                            </Button>
                            {designation.status ? (
                                <Button
                                    color="danger"
                                    size="sm"
                                    title="Delete"
                                    onClick={() => openDeleteModal(designation)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            ) : (
                                <Button
                                    color="success"
                                    size="sm"
                                    title="Restore"
                                    onClick={() => handleRestore(designation)}
                                >
                                    <RefreshCw size={14} />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        []
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Designation Master" pageTitle="Designations" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">Designations</h5>
                                <div className="d-flex gap-2">
                                    <FormGroup check className="mb-0 d-flex align-items-center">
                                        <Label check className="me-2 mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={showInactive}
                                                onChange={() => setShowInactive(!showInactive)}
                                            />
                                            <span className="ms-1">Show Inactive</span>
                                        </Label>
                                    </FormGroup>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchDesignations()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={openAddModal}
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add Designation
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <i className="ri-error-warning-line align-middle me-2"></i>
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading designations...</span>
                                    </div>
                                ) : designations.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-price-tag-line display-4 text-muted"></i>
                                        <h5 className="mt-3">No designations found</h5>
                                        <p className="text-muted">
                                            {showInactive
                                                ? "No designations match your filter criteria."
                                                : "Click 'Add Designation' to create your first designation."}
                                        </p>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={designations}
                                        customPageSize={10}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Add/Edit Modal */}
            <Modal isOpen={modal} toggle={closeModal} size="lg">
                <ModalHeader toggle={closeModal}>
                    {modalMode === 'add' ? 'Add New Designation' : 'Edit Designation'}
                </ModalHeader>
                <ModalBody>
                    {formError && (
                        <Alert color="danger" className="mb-3">
                            {formError}
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Title *</Label>
                                    <Input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="Enter designation title"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Description</Label>
                                    <Input
                                        type="textarea"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        rows={3}
                                        placeholder="Enter description (optional)"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="status"
                                            checked={formData.status}
                                            onChange={handleInputChange}
                                        />
                                        <span className="ms-2">Active</span>
                                    </Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeModal} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            modalMode === 'add' ? 'Create' : 'Update'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    <p>
                        Are you sure you want to delete the designation{' '}
                        <strong>"{designationToDelete?.title}"</strong>?
                    </p>
                    <Alert color="warning" className="mb-0">
                        <i className="ri-alert-line me-2"></i>
                        This action will deactivate the designation. You can restore it later.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* View Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)}>
                <ModalHeader toggle={() => setViewModal(false)}>
                    Designation Details
                </ModalHeader>
                <ModalBody>
                    {viewingDesignation && (
                        <div>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Title:</strong>
                                </Col>
                                <Col md={6}>
                                    {viewingDesignation.title}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Description:</strong>
                                </Col>
                                <Col md={6}>
                                    {viewingDesignation.description || '—'}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Status:</strong>
                                </Col>
                                <Col md={6}>
                                    {getStatusBadge(viewingDesignation.status)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Created At:</strong>
                                </Col>
                                <Col md={6}>
                                    {formatDate(viewingDesignation.created_at)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Updated At:</strong>
                                </Col>
                                <Col md={6}>
                                    {formatDate(viewingDesignation.updated_at)}
                                </Col>
                            </Row>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setViewModal(false)}>
                        Close
                    </Button>
                    {viewingDesignation && (
                        <Button
                            color="primary"
                            onClick={() => {
                                setViewModal(false);
                                openEditModal(viewingDesignation);
                            }}
                        >
                            <Edit size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default DesignationMaster;